import {createSlice} from '@reduxjs/toolkit';
import {removeReadySeenAt, removeAsapFlag} from '../orderTracker/utils/persist';
import {
  OrderTrackerState,
  OrderTrackerOrder,
  UserOrderId,
} from '../orderTracker';
import {fetchActiveOrders} from '../orderTracker';
import {
  addUserOrderId,
  pruneExpiredOrders,
  updateOrderStatus,
  removeOrder,
  clearAllOrders,
} from '../orderTracker';
import {keepOrderByTTL} from '../orderTracker/domain/filters';
import {ORDER_TRACKER_CONFIG} from '../../config/orderTracker';

const initialState: OrderTrackerState = {
  userOrderIds: [],
  activeOrders: [],
  loading: false,
  error: null,
  lastUpdated: null,
};

const orderTrackerSlice = createSlice({
  name: 'orderTracker',
  initialState,
  reducers: {
    // Reset error
    resetError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // fetchActiveOrders
    builder
      .addCase(fetchActiveOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActiveOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.activeOrders = action.payload;
        state.lastUpdated = Date.now();

        const now = Date.now();
        const ttlMs =
          ORDER_TRACKER_CONFIG.READY_HIDE_TTL_MIN *
          ORDER_TRACKER_CONFIG.MINUTES_IN_MS;

        state.activeOrders = state.activeOrders.filter((order) =>
          keepOrderByTTL(now, order, ttlMs),
        );
      })
      .addCase(fetchActiveOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // addUserOrderId
    builder.addCase(addUserOrderId.fulfilled, (state, action) => {
      const exists = state.userOrderIds.some((u) => u.id === action.payload.id);
      if (!exists) {
        state.userOrderIds.push(action.payload);
      }
      // Optimistic ASAP UX: if a matching active order exists, force OrderNow behavior immediately
      const hinted = action.payload;
      const matchOrder = (o: OrderTrackerOrder) => {
        const txIdStr = String(o.id);
        const transIdStr = String(o.transId || '');
        const transDateStr = String(o.transDate || '');
        const hid = String(hinted.id);
        const htrans = String(hinted.transId || '');
        const hdate = String(hinted.transDate || '');
        // also match by internal transId if available
        return (
          hid === txIdStr ||
          htrans === txIdStr ||
          (!!hdate && hdate === transDateStr) ||
          hid === transIdStr ||
          htrans === transIdStr
        );
      };
      if (hinted?.isAsapSelected) {
        state.activeOrders = state.activeOrders.map((o) => {
          if (matchOrder(o)) {
            return {
              ...o,
              orderType: 'OrderNow',
              etaText: undefined,
            } as OrderTrackerOrder;
          }
          return o;
        });
      }
    });

    // pruneExpiredOrders
    builder.addCase(pruneExpiredOrders.fulfilled, (state, action) => {
      // Remove orders by IDs returned from thunk
      const orderIdsToRemove = action.payload || [];
      state.activeOrders = state.activeOrders.filter(
        (order) => !orderIdsToRemove.includes(order.id),
      );
    });

    // updateOrderStatus
    builder.addCase(updateOrderStatus.fulfilled, (state, action) => {
      const {orderId} = action.payload;
      const order = state.activeOrders.find((o) => o.id === orderId);
      if (order) {
        // Update logic would go here
      }
    });

    // removeOrder
    builder.addCase(removeOrder.fulfilled, (state, action) => {
      const orderId = action.payload;
      state.activeOrders = state.activeOrders.filter((o) => o.id !== orderId);
      state.userOrderIds = state.userOrderIds.filter((u) => u.id !== orderId);
      removeReadySeenAt(orderId);
      removeAsapFlag(orderId);
    });

    // clearAllOrders
    builder.addCase(clearAllOrders.fulfilled, (state) => {
      state.activeOrders = [];
      state.userOrderIds = [];
      state.lastUpdated = null;
    });
  },
});

export const {resetError} = orderTrackerSlice.actions;

// Export thunks
export {
  fetchActiveOrders,
  addUserOrderId,
  pruneExpiredOrders,
  updateOrderStatus,
  removeOrder,
  clearAllOrders,
};

// Export types
export type {OrderTrackerState, OrderTrackerOrder, UserOrderId};

export default orderTrackerSlice.reducer;
